/**
 * @fileoverview The client-log ingest's wire contract, as the SENDER must honour it
 * (UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-13).
 *
 * `@puffin-core/client-log-routes` refuses a batch WHOLE: one entry the closed Joi schema rejects,
 * 51 entries, or 64 KiB + 1 byte, and the ingest answers 400 / 413 for every entry in it. Before
 * this module the transport shipped whatever the logger produced, so a single `logger.error('')`,
 * a 2,001-character message or a `logger.warn('x', [1, 2])` poisoned its batch — and because a
 * failed flush RESTORED the batch, it poisoned every retry after it too. The same restore grew the
 * buffer past 50 entries after any transient outage, and every later flush then sent the whole
 * buffer — a batch the schema refuses on size alone, forever, for the life of the page (measured
 * 2026-09-23: 70 entries in one POST after a 20-entry outage).
 *
 * So the sender conforms each entry and cuts each batch to the server's bounds BEFORE it posts.
 * The bounds are mirrored, not imported: the producer is ESM browser code and the contract is a
 * CJS API package. `@puffin-core/client-log-routes`'s constants suite reads THIS file's source and
 * fails when the two sides drift, the same way it already pins `LOG_LEVEL`.
 *
 * @module transports/wireContract
 */

/**
 * The ingest's bounds (`@puffin-core/client-log-routes` `constants.js` + `clientLogSchema.js`).
 * @constant {Readonly<{maxLogsPerBatch: number, maxMessageLength: number, maxUrlLength: number, maxBodyBytes: number}>}
 */
export const WIRE_LIMITS = Object.freeze({
  maxLogsPerBatch: 50,
  maxMessageLength: 2000,
  maxUrlLength: 2048,
  maxBodyBytes: 65536,
});

/**
 * The MEASUREMENT kinds an entry can be — a Core Web Vital (`./collectWebVitals.js`) or a client
 * timing (`Logger#timing`). Sent as `measurement` so the ingest can record it as a METRIC as well as a
 * line (F-TOCONTAIN-3 U2c-2); mirrored by `@puffin-core/client-log-routes` `CLIENT_MEASUREMENTS`, whose
 * constants suite reads this declaration.
 * @constant {readonly string[]}
 */
export const MEASUREMENTS = Object.freeze(['web-vital', 'timing']);

const HEX32 = /^[0-9a-f]{32}$/;
const HEX16 = /^[0-9a-f]{16}$/;
/** Carriers the schema pattern-checks; a malformed one is omitted, never sent to be refused. */
const PATTERNED_CARRIERS = Object.freeze({ traceId: HEX32, spanId: HEX16, correlationId: HEX32 });
/** Logger entry fields that are wire fields; everything else on an entry is app context. */
const WIRE_FIELDS = Object.freeze([
  'timestamp',
  'level',
  'message',
  'data',
  'traceId',
  'spanId',
  'correlationId',
  'url',
]);
// `sampled` is the shipping DECISION's input (the trace's sampled flag), never payload.
const NON_CONTEXT_FIELDS = new Set([...WIRE_FIELDS, 'source', 'sampled']);
const TRUNCATION_MARK = '…';
/** What an entry with no message says, because the schema refuses `''`. */
export const EMPTY_MESSAGE = '(empty log message)';
const BATCH_ENVELOPE_BYTES = utf8ByteLength('{"source":"client","logs":[]}');

/**
 * UTF-8 length of a string — what the ingest's Content-Length gate counts. Hand-counted rather
 * than `TextEncoder`/`Blob` so it is synchronous, allocation-free and identical in every host.
 *
 * @param {string} text
 * @returns {number}
 */
export function utf8ByteLength(text) {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      // A surrogate PAIR is one 4-byte code point; a lone surrogate is encoded as U+FFFD (3).
      bytes += 4;
      i += 1;
    } else bytes += 3;
  }
  return bytes;
}

function truncate(text, max) {
  return text.length > max
    ? `${text.slice(0, max - TRUNCATION_MARK.length)}${TRUNCATION_MARK}`
    : text;
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function conformMessage(message) {
  const text = typeof message === 'string' ? message : String(message ?? '');
  return text.length === 0 ? EMPTY_MESSAGE : truncate(text, WIRE_LIMITS.maxMessageLength);
}

function conformCarrier(key, value) {
  if (key === 'message') return conformMessage(value);
  if (key === 'url')
    return typeof value === 'string' ? truncate(value, WIRE_LIMITS.maxUrlLength) : undefined;
  if (key === 'data') return isPlainObject(value) ? value : { value };
  const pattern = PATTERNED_CARRIERS[key];
  if (pattern) return typeof value === 'string' && pattern.test(value) ? value : undefined;
  return value;
}

/**
 * Map a logger entry onto the ingest's closed wire shape: known fields stay (each conformed to its
 * schema rule), everything else becomes `context`. Absent/null carriers are omitted — the schema
 * refuses a `null` where it expects a string.
 *
 * @param {Object} entry - a logger entry
 * @returns {Object} a wire entry the ingest schema accepts
 */
export function toWireEntry(entry) {
  const wire = {};
  for (const key of WIRE_FIELDS) {
    const raw = entry[key];
    const present = raw !== undefined && raw !== null;
    // `message` is REQUIRED by the schema, so an absent one is conformed rather than omitted.
    if (!present && key !== 'message') continue;
    const value = conformCarrier(key, raw);
    if (value !== undefined) wire[key] = value;
  }
  const context = {};
  let hasContext = false;
  for (const [key, value] of Object.entries(entry)) {
    if (!NON_CONTEXT_FIELDS.has(key)) {
      context[key] = value;
      hasContext = true;
    }
  }
  if (hasContext) wire.context = context;
  if (MEASUREMENTS.includes(entry.source)) wire.measurement = entry.source;
  return wire;
}

/** `JSON.stringify`, or `null` for a value it throws on (a BigInt, a cycle in app context). */
function serialize(wire) {
  try {
    return JSON.stringify(wire);
  } catch {
    return null;
  }
}

/**
 * The serialized size an entry adds to a batch body. An entry that could not fit a batch even
 * ALONE — or whose `data`/`context` cannot be serialized at all — loses its `data` and `context`
 * (the message, level and join keys survive) so it still ships and says why, instead of being
 * refused with 413 on every retry or throwing out of `log()` into the page that logged it.
 *
 * @param {Object} wire - a wire entry
 * @returns {{wire: Object, bytes: number}}
 */
export function sizeWireEntry(wire) {
  const json = serialize(wire);
  const bytes = json === null ? null : utf8ByteLength(json);
  if (bytes !== null && BATCH_ENVELOPE_BYTES + bytes <= WIRE_LIMITS.maxBodyBytes) {
    return { wire, bytes };
  }
  const { data: _data, context: _context, ...kept } = wire;
  const data =
    bytes === null
      ? { unserializable: 'entry data or context could not be serialized' }
      : { truncated: 'entry exceeded the ingest body limit', bytes };
  const shrunk = { ...kept, data };
  return { wire: shrunk, bytes: utf8ByteLength(JSON.stringify(shrunk)) };
}

/**
 * How many leading entries of a queue fit ONE batch: at most `maxLogsPerBatch` (and the caller's
 * own batch size), and a serialized body within `maxBodyBytes`. Always at least one when the
 * queue is non-empty — `sizeWireEntry` guarantees a lone entry fits.
 *
 * @param {Array<{bytes: number}>} queue - sized entries, oldest first
 * @param {number} batchSize - the transport's own ceiling
 * @returns {number}
 */
export function takeableCount(queue, batchSize) {
  const ceiling = Math.min(batchSize, WIRE_LIMITS.maxLogsPerBatch, queue.length);
  let bytes = BATCH_ENVELOPE_BYTES;
  let count = 0;
  while (count < ceiling) {
    const next = bytes + queue[count].bytes + (count > 0 ? 1 : 0);
    if (count > 0 && next > WIRE_LIMITS.maxBodyBytes) break;
    bytes = next;
    count += 1;
  }
  return count;
}
